import { createContext, useContext, useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useUpdateUserPreferences, useUserPreferences } from '@hooks/user/use-user-preferences';
import { SESSION_CLEARED_EVENT } from '@utils/session-events';
import { readHasSession, useHasSession } from '@hooks/use-has-session';
import {
  colors,
  type ThemeMode,
  DARK_THEME_MODES,
  SELECTABLE_THEME_MODES,
  DEFAULT_THEME_MODE,
} from '@constants/colors';

const DEFAULT_SHADCN_VARS: Record<string, string> = {
  '--background': '0 0% 100%',
  '--foreground': '222.2 84% 4.9%',
  '--card': '0 0% 100%',
  '--card-foreground': '222.2 84% 4.9%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '222.2 84% 4.9%',
  '--primary': '222.2 47.4% 11.2%',
  '--primary-foreground': '210 40% 98%',
  '--secondary': '210 40% 96.1%',
  '--secondary-foreground': '222.2 47.4% 11.2%',
  '--muted': '210 40% 96.1%',
  '--muted-foreground': '215.4 16.3% 46.9%',
  '--accent': '210 40% 96.1%',
  '--accent-foreground': '222.2 47.4% 11.2%',
  '--destructive': '0 84.2% 60.2%',
  '--destructive-foreground': '210 40% 98%',
  '--border': '214.3 31.8% 91.4%',
  '--input': '214.3 31.8% 91.4%',
  '--ring': '222.2 84% 4.9%',
};

const DEFAULT_CHART_COLORS = ['12 76% 61%', '173 58% 39%', '197 37% 24%', '43 74% 66%', '27 87% 67%'];

const HSL_TRIPLET_REGEX = /^\s*\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%\s*$/;

const ALL_THEME_MODES = Object.keys(colors) as ThemeMode[];

const parseHslString = (value: string): string | null => {
  const matches = value.match(/hsl[a]?\(([^)]+)\)/i);
  if (!matches) {
    return null;
  }
  const parts = matches[1]
    .split(/[ ,/]+/)
    .filter(Boolean)
    .slice(0, 3);
  if (parts.length < 3) {
    return null;
  }
  const [h, s, l] = parts;
  const normalizedS = s.includes('%') ? s : `${s}%`;
  const normalizedL = l.includes('%') ? l : `${l}%`;
  return `${h} ${normalizedS} ${normalizedL}`;
};

const rgbToHslTriplet = (r: number, g: number, b: number): string => {
  const normalizedR = r / 255;
  const normalizedG = g / 255;
  const normalizedB = b / 255;

  const max = Math.max(normalizedR, normalizedG, normalizedB);
  const min = Math.min(normalizedR, normalizedG, normalizedB);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case normalizedR:
        h = (normalizedG - normalizedB) / d + (normalizedG < normalizedB ? 6 : 0);
        break;
      case normalizedG:
        h = (normalizedB - normalizedR) / d + 2;
        break;
      default:
        h = (normalizedR - normalizedG) / d + 4;
        break;
    }
    h /= 6;
  }

  const hue = Math.round(h * 360);
  const saturation = Math.round(s * 100);
  const lightness = Math.round(l * 100);

  return `${hue} ${saturation}% ${lightness}%`;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((char) => char + char)
      .join('');
  }

  if (normalized.length !== 6) {
    return null;
  }

  const intValue = Number.parseInt(normalized, 16);
  if (Number.isNaN(intValue)) {
    return null;
  }

  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255,
  };
};

const convertColorToHsl = (color: string | undefined, fallback: string): string => {
  if (!color) {
    return fallback;
  }

  const trimmed = color.trim();

  if (HSL_TRIPLET_REGEX.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('hsl')) {
    const parsed = parseHslString(trimmed);
    if (parsed) {
      return parsed;
    }
  }

  if (trimmed.startsWith('#')) {
    const rgb = hexToRgb(trimmed);
    if (rgb) {
      return rgbToHslTriplet(rgb.r, rgb.g, rgb.b);
    }
  }

  if (trimmed.startsWith('rgb')) {
    const match = trimmed.match(/rgba?\(([^)]+)\)/i);
    if (match) {
      const [r, g, b] = match[1]
        .split(',')
        .map((value) => Number.parseFloat(value.trim()))
        .slice(0, 3);

      if (![r, g, b].some((component) => Number.isNaN(component))) {
        return rgbToHslTriplet(r, g, b);
      }
    }
  }

  return fallback;
};

const isThemeMode = (value: string | null): value is ThemeMode =>
  !!value && (ALL_THEME_MODES as string[]).includes(value);

/**
 * APEX rebrand (Phase 2): every saved theme is force-migrated onto the brand
 * family on first load, preserving only the light/dark intent (dark modes →
 * apex-night, light/retired modes → apex). The switcher stays available
 * afterwards for users who want a Horizon look back.
 */
const resolveInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_MODE;
  }

  const saved = localStorage.getItem('themeMode');
  // `gradient` was a dark palette alias removed before the Horizon rollout
  const normalized = saved === 'gradient' ? 'purple' : saved;

  const resolved: ThemeMode = isThemeMode(normalized)
    ? DARK_THEME_MODES.includes(normalized)
      ? 'apex-night'
      : 'apex'
    : DEFAULT_THEME_MODE;

  if (resolved !== saved) {
    localStorage.setItem('themeMode', resolved);
  }
  return resolved;
};

interface ThemeContextType {
  mode: ThemeMode;
  /** True for every dark palette (dark, horizon-night, dusk, …), not just mode === 'dark'. */
  isDark: boolean;
  toggleTheme: (themeMode?: ThemeMode) => void;
  colors: any;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>(resolveInitialTheme);
  // Same persistence story as language and text size: localStorage for first
  // paint, the profile for the next device, adopted once per session so a
  // refetch never undoes a choice the user just made.
  // State, not a read at render: an SPA sign-in must enable the query.
  const hasSession = useHasSession();
  const { data: preferences } = useUserPreferences({ enabled: hasSession });
  const savePreference = useUpdateUserPreferences();
  const adoptedFromProfile = useRef(false);

  useEffect(() => {
    const stored = preferences?.themeMode ?? null;
    if (adoptedFromProfile.current || !stored || !isThemeMode(stored)) return;
    if (!SELECTABLE_THEME_MODES.includes(stored)) return;
    adoptedFromProfile.current = true;
    setMode(stored);
  }, [preferences?.themeMode]);

  useEffect(() => {
    const onSessionCleared = () => {
      adoptedFromProfile.current = false;
    };
    window.addEventListener(SESSION_CLEARED_EVENT, onSessionCleared);
    return () => window.removeEventListener(SESSION_CLEARED_EVENT, onSessionCleared);
  }, []);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('themeMode', mode);

    // Apply theme to document root and body
    const root = document.documentElement;
    const bodyElement = document.body;
    root.setAttribute('data-theme', mode);
    bodyElement.setAttribute('data-theme', mode);

    const isDarkTheme = DARK_THEME_MODES.includes(mode);
    root.classList.toggle('dark', isDarkTheme);
    bodyElement.classList.toggle('dark', isDarkTheme);

    // Inject CSS variables for table footer
    const palette = colors[mode] as any;
    root.style.setProperty('--jarvis-table-footer-bg', palette.tableFooter);
    root.style.setProperty('--jarvis-table-footer-text', palette.tableFooterText);
    root.style.setProperty('--jarvis-border', palette.border);

    // Atmospheric shell gradients (horizon family and any future gradient themes)
    const bgGradient = palette.backgroundGradient as string | undefined;
    const sidebarGradient = (palette.sidebarGradient as string | undefined) ?? bgGradient;
    if (bgGradient) {
      root.style.setProperty('--jarvis-bg-gradient', bgGradient);
      bodyElement.style.backgroundImage = bgGradient;
      bodyElement.style.backgroundAttachment = 'fixed';
      bodyElement.style.backgroundColor = palette.background;
    } else {
      root.style.removeProperty('--jarvis-bg-gradient');
      bodyElement.style.removeProperty('background-image');
      bodyElement.style.removeProperty('background-attachment');
    }
    if (sidebarGradient) {
      root.style.setProperty('--jarvis-sidebar-gradient', sidebarGradient);
    } else {
      root.style.removeProperty('--jarvis-sidebar-gradient');
    }

    // Primary fill / foreground follow shadcn contrast rules:
    // light → dark fill + light text; dark/jarvis → light fill + dark text.
    const primaryFill = palette.buttonBackground ?? palette.primary ?? palette.accent ?? '#4F46E5';
    const primaryForeground = palette.buttonText ?? (isDarkTheme ? '#0B1120' : '#FFFFFF');

    const shadcnVarValues: Record<string, string> = {
      '--background': palette.background,
      '--foreground': palette.text,
      '--card': palette.card ?? palette.background,
      '--card-foreground': palette.text,
      '--popover': palette.card ?? palette.background,
      '--popover-foreground': palette.text,
      '--primary': primaryFill,
      '--primary-foreground': primaryForeground,
      '--secondary': palette.sidebar ?? palette.background,
      '--secondary-foreground': palette.text,
      '--muted': palette.formFields ?? palette.background,
      '--muted-foreground': palette.textSecondary ?? palette.text,
      '--accent': palette.accent ?? palette.primary,
      '--accent-foreground': primaryForeground,
      '--destructive': palette.error ?? '#EF4444',
      '--destructive-foreground': '#FFFFFF',
      '--border': palette.border ?? '#E5E7EB',
      // shadcn `--input` is the control border color
      '--input': palette.inputBorder ?? palette.border ?? '#E5E7EB',
      // Prefer brand accent/border over near-white primary fills on dark/jarvis
      // so focus rings stay soft (Input/Textarea/Combobox use ring-ring/40).
      '--ring': palette.accent ?? palette.primary ?? palette.border ?? primaryFill,
    };

    // Jarvis field tokens (used by the JarvisTextInput wrapper)
    root.style.setProperty('--jarvis-input-bg', palette.inputBackground ?? palette.formFields ?? palette.background);
    root.style.setProperty('--jarvis-input-fg', palette.inputText ?? palette.text);
    root.style.setProperty('--jarvis-input-border', palette.inputBorder ?? palette.border ?? '#E5E7EB');
    root.style.setProperty(
      '--jarvis-input-placeholder',
      palette.inputPlaceholder ?? palette.placeholder ?? palette.textSecondary ?? '#9CA3AF'
    );

    // Brand tokens for Tailwind arbitrary values (hardcoded color sweep):
    // --jarvis-accent = interactive accent (text/links/focus),
    // --jarvis-cta = filled button surface, --jarvis-accent-soft = tinted wash.
    root.style.setProperty('--jarvis-accent', palette.accent ?? palette.primary ?? '#2563EB');
    root.style.setProperty('--jarvis-cta', palette.buttonBackground ?? palette.accent ?? palette.primary ?? '#2563EB');
    root.style.setProperty(
      '--jarvis-accent-soft',
      palette.dropdownHover ?? palette.formFields ?? 'rgba(37, 99, 235, 0.08)'
    );

    for (const [variable, value] of Object.entries(shadcnVarValues)) {
      const fallback = DEFAULT_SHADCN_VARS[variable] ?? DEFAULT_SHADCN_VARS['--background'];
      root.style.setProperty(variable, convertColorToHsl(value, fallback));
    }

    const chartPalette = [
      palette.primary ?? palette.accent,
      palette.success ?? palette.accent,
      palette.info ?? palette.primary,
      palette.warning ?? palette.accent,
      palette.accent ?? palette.primary,
    ];

    chartPalette.forEach((color, index) => {
      const fallback = DEFAULT_CHART_COLORS[index] ?? DEFAULT_CHART_COLORS[0];
      root.style.setProperty(`--chart-${index + 1}`, convertColorToHsl(color, fallback));
    });

    // Apply Jarvis theme font family (Manus-inspired: Inter + Libre Baskerville)
    const defaultFontFamily = 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';
    const jarvisFontFamily = "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    const jarvisHeadingFont = "'Libre Baskerville', Georgia, 'Times New Roman', serif";

    if (mode === 'jarvis') {
      root.style.setProperty('--mantine-font-family', jarvisFontFamily);
      root.style.setProperty('--mantine-font-family-headings', jarvisHeadingFont);
      bodyElement.style.fontFamily = jarvisFontFamily;
      // Align default Mantine primary (violet) filled buttons with jarvis white primary.
      // Text color is handled via globals.css so --mantine-color-white stays intact.
      root.style.setProperty('--mantine-color-violet-filled', palette.buttonBackground ?? '#FAFAFA');
      root.style.setProperty('--mantine-color-violet-filled-hover', palette.buttonHover ?? '#E4E4E7');
      root.style.setProperty('--mantine-color-violet-light', 'rgba(250, 250, 250, 0.12)');
      root.style.setProperty('--mantine-color-violet-light-hover', 'rgba(250, 250, 250, 0.18)');
      root.style.setProperty('--mantine-color-violet-light-color', '#FAFAFA');
      root.style.setProperty('--mantine-color-violet-outline', '#FAFAFA');
      root.style.setProperty('--mantine-color-violet-outline-hover', 'rgba(250, 250, 250, 0.08)');
    } else {
      root.style.removeProperty('--mantine-font-family');
      root.style.removeProperty('--mantine-font-family-headings');
      bodyElement.style.fontFamily = defaultFontFamily;
      root.style.removeProperty('--mantine-color-violet-filled');
      root.style.removeProperty('--mantine-color-violet-filled-hover');
      root.style.removeProperty('--mantine-color-violet-light');
      root.style.removeProperty('--mantine-color-violet-light-hover');
      root.style.removeProperty('--mantine-color-violet-light-color');
      root.style.removeProperty('--mantine-color-violet-outline');
      root.style.removeProperty('--mantine-color-violet-outline-hover');
    }

    // Dispatch custom event for theme change
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: { mode } }));
  }, [mode]);

  const toggleTheme = (themeMode?: ThemeMode) => {
    let next: ThemeMode;
    if (themeMode && isThemeMode(themeMode)) {
      next = themeMode;
    } else {
      // Cycle brand selectable themes: Horizon ↔ Horizon Night
      const selectable = SELECTABLE_THEME_MODES;
      const currentIndex = selectable.indexOf(mode);
      next =
        currentIndex === -1
          ? selectable[0] ?? DEFAULT_THEME_MODE
          : selectable[(currentIndex + 1) % selectable.length] ?? DEFAULT_THEME_MODE;
    }
    setMode(next);
    // A choice made now wins over whatever the profile says later this session.
    adoptedFromProfile.current = true;
    if (readHasSession()) {
      // Fire-and-forget: the switch already happened locally.
      savePreference.mutate({ themeMode: next });
    }
  };

  const currentColors = useMemo(() => {
    const palette = colors[mode] as any;

    return {
      ...palette,
      buttonBackground: palette.buttonBackground ?? palette.button ?? palette.accent,
      buttonText: palette.buttonText ?? palette.text,
      buttonBorder: palette.buttonBorder ?? palette.border,
      buttonHover: palette.buttonHover ?? palette.primaryHover ?? palette.accent,
    };
  }, [mode]);

  const isDark = DARK_THEME_MODES.includes(mode);

  return (
    <ThemeContext.Provider value={{ mode, isDark, toggleTheme, colors: currentColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
